import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, RefreshCw, Unplug, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const FanvueConnectionCard = ({ onChange }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState('');

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      axios.get(`${API_URL}/chatting/fanvue/status`),
      axios.get(`${API_URL}/chatting/settings`),
    ]);
    setStatus(s.data);
    setRate(String(r.data.rate_percent));
  }, []);

  useEffect(() => { load().catch(() => toast.error('Failed to load Fanvue status')); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const connect = () => act(async () => {
    const r = await axios.post(`${API_URL}/chatting/fanvue/connect`);
    window.location.href = r.data.url;
  });
  const sync = () => act(() => axios.post(`${API_URL}/chatting/sync`), 'Fanvue data refreshed');
  const disconnect = () => window.confirm('Disconnect Fanvue? Stats and chatter invoices will stop.') &&
    act(() => axios.post(`${API_URL}/chatting/fanvue/disconnect`), 'Fanvue disconnected');
  const saveRate = () => act(() => axios.put(`${API_URL}/chatting/settings`, { rate_percent: parseFloat(rate) }), 'Chatter rate saved');

  const connected = status?.connected;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-card p-5 rounded-md border border-border space-y-3" data-testid="fanvue-connection-card">
        <div className="flex items-center gap-2">
          {connected ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
          <span className="text-white font-semibold" data-testid="fanvue-status">
            {connected ? `Fanvue connected${status.account_name ? ` · ${status.account_name}` : ''}`
              : status?.status === 'disconnected' ? 'Fanvue connection lost' : 'Fanvue not connected'}
          </span>
        </div>
        {status?.last_sync_at && <div className="text-xs text-zinc-500">Last sync {new Date(status.last_sync_at).toLocaleString()}</div>}
        {status?.last_error && !connected && <div className="text-xs text-red-400 break-all">{status.last_error}</div>}
        {status?.attribution_error && (
          <div className="text-xs text-red-400 break-all" data-testid="fanvue-attribution-error">
            Tips/chargebacks sync: {status.attribution_error}
            {status.scope && <div className="text-zinc-500 mt-1">Granted scopes: {status.scope}</div>}
          </div>
        )}
        {status?.attribution_synced_at && !status?.attribution_error && (
          <div className="text-xs text-zinc-500" data-testid="fanvue-attribution-synced">
            Tips/chargebacks matched {new Date(status.attribution_synced_at).toLocaleString()}
          </div>
        )}
        {status && !status.configured && (
          <div className="text-xs text-amber-400" data-testid="fanvue-not-configured">Fanvue app credentials are missing on the server.</div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={connect} disabled={busy || !status?.configured} className="bg-purple-600 hover:bg-purple-700" data-testid="fanvue-connect-btn">
            <Link2 className="w-4 h-4 mr-2" />{connected ? 'Reconnect Fanvue' : 'Connect Fanvue'}
          </Button>
          {connected && (
            <>
              <Button variant="outline" onClick={sync} disabled={busy} className="border-zinc-700 text-zinc-300" data-testid="fanvue-sync-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} />Refresh now
              </Button>
              <Button variant="outline" onClick={disconnect} disabled={busy} className="border-red-500/40 text-red-400" data-testid="fanvue-disconnect-btn">
                <Unplug className="w-4 h-4 mr-2" />Disconnect
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="bg-card p-5 rounded-md border border-border space-y-3" data-testid="chatter-rate-card">
        <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Chatter pay rate</div>
        <div className="text-xs text-zinc-500">(PPV + tips − chargebacks) × rate, invoiced every Monday</div>
        <div className="flex gap-2">
          <Input type="number" step="0.1" min="0" max="100" value={rate} onChange={e => setRate(e.target.value)}
            className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="chatter-rate-input" />
          <span className="self-center text-zinc-400">%</span>
          <Button onClick={saveRate} disabled={busy || rate === ''} data-testid="chatter-rate-save-btn">Save</Button>
        </div>
      </div>
    </div>
  );
};
